/**
 * Fenêtre « État de paiement » d'un étudiant (2026-09-12, partagée le 2026-09-13).
 *
 * Période par période : attendu, payé, reliquat, périodes non concernées ; aménagement
 * individuel des paiements (étudiants arrivés en cours d'année) et accès léger
 * individuel aux résultats. Utilisée par Accès étudiants et Demandes d'activation.
 */
import React, { useEffect, useState } from 'react';
import {
  useGetEtatPaiementEtudiantQuery,
  useEnregistrerAmenagementMutation,
  useRetirerAmenagementMutation,
  useDefinirAccesLegerMutation,
  useModifierEncaissementMutation,
  useAnnulerEncaissementMutation,
} from '../../services/portailAdminApi';
import type { EncaissementManuelInfo } from '../../services/portailAdminApi';

export interface EtudiantPaiementRef {
  matricule: string;
  nom?: string;
  prenoms?: string;
  classe?: string;
}

const STATUT_PERIODE: Record<string, { label: string; cls: string }> = {
  a_jour: { label: 'Réglé', cls: 'text-green-700 dark:text-green-400' },
  reliquat: { label: 'Reliquat', cls: 'text-orange-700 dark:text-orange-400' },
  impaye: { label: 'Impayé', cls: 'text-red-700 dark:text-red-400' },
  non_enregistre: { label: 'Non enregistré', cls: 'text-red-700 dark:text-red-400' },
  non_concerne: { label: 'Non concerné', cls: 'text-gray-500 dark:text-gray-400' },
};

const LIBELLES_ACCES: Record<string, string> = {
  a_jour: 'Accès aux résultats autorisé',
  force_admin: 'Accès forcé par un administrateur',
  sous_contrat: 'Sous contrat (accès sans condition de paiement)',
  bloque_dette: 'Accès bloqué : paiements à régulariser',
  bloque_absent: 'Accès bloqué : paiement non enregistré',
};

const MOIS_SCOLAIRES = ['NOV', 'DEC', 'JAN', 'FEV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOUT'];

function fcfa(s: string | null | undefined): string {
  const n = parseFloat(s || '0');
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

function dateCourte(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

export const EtatPaiementModal: React.FC<{ etudiant: EtudiantPaiementRef; onClose: () => void }> = ({ etudiant, onClose }) => {
  const { data, isFetching, error } = useGetEtatPaiementEtudiantQuery(etudiant.matricule, {
    refetchOnMountOrArgChange: true,
  });
  const [enregistrer, { isLoading: enregistrement }] = useEnregistrerAmenagementMutation();
  const [retirer, { isLoading: retrait }] = useRetirerAmenagementMutation();
  const [definirAccesLeger, { isLoading: majLeger }] = useDefinirAccesLegerMutation();
  const [edition, setEdition] = useState(false);
  const [nonConcernees, setNonConcernees] = useState<Set<string>>(new Set());
  const [montants, setMontants] = useState<Record<string, string>>({});
  const [motif, setMotif] = useState('');
  const [formLeger, setFormLeger] = useState(false);
  const [motifLeger, setMotifLeger] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);

  const amenagement = data?.amenagement || null;
  const accesLeger = data?.acces_leger_individuel || null;
  const identite = data?.etudiant;
  const nomAffiche = (identite
    ? `${identite.nom} ${identite.prenoms}`
    : `${etudiant.nom || ''} ${etudiant.prenoms || ''}`).trim() || etudiant.matricule;
  const classeAffichee = identite?.classe || etudiant.classe || '';

  useEffect(() => {
    if (edition) return;
    setNonConcernees(new Set(amenagement?.periodes_exemptees || []));
    setMontants({ ...(amenagement?.montants_exiges || {}) });
    setMotif(amenagement?.motif || '');
  }, [amenagement, edition]);

  const basculer = (code: string) => setNonConcernees((prev) => {
    const s = new Set(prev);
    if (s.has(code)) s.delete(code); else s.add(code);
    return s;
  });

  // Raccourci : mois d'arrivée → les mois antérieurs ne concernent pas l'étudiant
  const appliquerArrivee = (mois: string) => {
    const idx = MOIS_SCOLAIRES.indexOf(mois);
    if (idx < 0) return;
    setNonConcernees((prev) => {
      const s = new Set(prev);
      MOIS_SCOLAIRES.forEach((m, i) => { if (i < idx) s.add(m); else s.delete(m); });
      return s;
    });
  };

  const sauver = async () => {
    setMessage(null);
    const montantsPropres: Record<string, string> = {};
    Object.entries(montants).forEach(([periode, valeur]) => {
      if (valeur !== '' && valeur !== undefined && valeur !== null && !nonConcernees.has(periode)) {
        montantsPropres[periode] = String(valeur);
      }
    });
    try {
      await enregistrer({
        matricule: etudiant.matricule,
        periodes_exemptees: Array.from(nonConcernees),
        montants_exiges: montantsPropres,
        motif: motif.trim(),
      }).unwrap();
      setEdition(false);
      setMessage({ ok: true, texte: "Aménagement enregistré. L'accès de l'étudiant est recalculé immédiatement." });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || 'Erreur' });
    }
  };

  const supprimer = async () => {
    if (!window.confirm(
      `Retirer l'aménagement de ${nomAffiche} ?\n\n`
      + "Toutes les périodes requises s'appliqueront de nouveau à cet étudiant.",
    )) return;
    setMessage(null);
    try {
      await retirer({ matricule: etudiant.matricule }).unwrap();
      setEdition(false);
      setMessage({ ok: true, texte: 'Aménagement retiré.' });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || 'Erreur' });
    }
  };

  const accorderLeger = async () => {
    setMessage(null);
    try {
      await definirAccesLeger({ matricule: etudiant.matricule, actif: true, motif: motifLeger.trim() }).unwrap();
      setFormLeger(false);
      setMotifLeger('');
      setMessage({
        ok: true,
        texte: "Accès léger accordé : tant qu'il reste redevable, l'étudiant voit sa décision et ses matières à rattraper, sans ses notes.",
      });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || 'Erreur' });
    }
  };

  const retirerLeger = async () => {
    if (!window.confirm(
      `Retirer l'accès léger de ${nomAffiche} ?\n\n`
      + "S'il reste redevable, il ne verra plus sa décision.",
    )) return;
    setMessage(null);
    try {
      await definirAccesLeger({ matricule: etudiant.matricule, actif: false }).unwrap();
      setMessage({ ok: true, texte: 'Accès léger retiré.' });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || 'Erreur' });
    }
  };

  const periodes = data?.periodes || [];
  const reste = parseFloat(data?.totaux.reste || '0');

  let effetLeger = '';
  if (data) {
    if (data.acces.autorise) {
      effetLeger = "Sans effet pour l'instant : l'étudiant a l'accès complet à ses résultats.";
    } else if (data.acces.acces_leger) {
      effetLeger = accesLeger
        ? "En vigueur : l'étudiant voit sa décision (validé ou non) et ses matières à rattraper, sans aucune note."
        : 'En vigueur par les périodes « accès léger » des paramètres du portail.';
    } else {
      effetLeger = "L'étudiant n'a actuellement aucun accès à ses résultats.";
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              État de paiement — {nomAffiche}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {etudiant.matricule}{classeAffichee ? ` · ${classeAffichee}` : ''}{data?.annee_academique ? ` · ${data.annee_academique}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isFetching && !data && <div className="text-center text-sm text-gray-500 py-8">Chargement…</div>}
          {error && !data && (
            <div className="text-sm text-red-700 dark:text-red-300">
              {(error as any)?.data?.detail || "Impossible de charger l'état de paiement."}
            </div>
          )}

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Accès</div>
                  <div className={`text-sm font-semibold ${data.acces.autorise ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {LIBELLES_ACCES[data.acces.mode] || data.acces.mode}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {data.acces.nb_a_jour}/{data.acces.nb_periodes_requises} période(s) requise(s) réglée(s)
                  </div>
                  {!data.acces.autorise && data.acces.acces_leger && (
                    <div className="text-[11px] text-sky-700 dark:text-sky-300">accès léger : décision sans notes</div>
                  )}
                </div>
                <div className="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Total attendu</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{fcfa(data.totaux.attendu)}</div>
                  <div className="text-[11px] text-gray-500">FCFA, périodes concernées</div>
                </div>
                <div className="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Total payé</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{fcfa(data.totaux.paye)}</div>
                  <div className="text-[11px] text-gray-500">FCFA</div>
                </div>
                <div className="rounded border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-[11px] uppercase text-gray-500">Reste à payer</div>
                  <div className={`text-lg font-semibold ${reste > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                    {fcfa(data.totaux.reste)}
                  </div>
                  <div className="text-[11px] text-gray-500">{data.nb_periodes_a_regler} période(s) à régler</div>
                </div>
              </div>

              {(data.sous_contrat || data.acces_force) && (
                <div className="text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded p-2">
                  {data.sous_contrat && 'Étudiant sous contrat : ses paiements ne conditionnent pas son accès. '}
                  {data.acces_force && "Accès forcé par un administrateur : ses paiements ne conditionnent pas son accès."}
                </div>
              )}

              {amenagement && !edition && (
                <div className="text-xs border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 rounded p-3 text-sky-900 dark:text-sky-200 space-y-0.5">
                  <div className="font-semibold">Aménagement {amenagement.annee_academique}</div>
                  {amenagement.periodes_exemptees.length > 0 && (
                    <div>Non concernés : {amenagement.periodes_exemptees.join(', ')}</div>
                  )}
                  {Object.keys(amenagement.montants_exiges).length > 0 && (
                    <div>
                      Montant exigé : {Object.entries(amenagement.montants_exiges)
                        .map(([p, m]) => `${p} ${fcfa(m)} FCFA`).join(' · ')}
                    </div>
                  )}
                  <div>Motif : {amenagement.motif || '—'}</div>
                  <div className="text-sky-700 dark:text-sky-300">
                    Par {amenagement.modifie_par_nom || '—'}
                    {amenagement.date_modification && ` le ${dateCourte(amenagement.date_modification)}`}
                  </div>
                </div>
              )}

              {/* Accès léger individuel (2026-09-13) */}
              {!edition && (
                <div className="text-xs border border-gray-200 dark:border-gray-700 rounded p-3 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-0.5 min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">Accès léger aux résultats</div>
                      {accesLeger ? (
                        <div className="text-gray-700 dark:text-gray-300">
                          Accordé individuellement pour {accesLeger.annee_academique} — motif : {accesLeger.motif || '—'}
                          <span className="text-gray-500 dark:text-gray-400">
                            {' '}· par {accesLeger.par_nom || '—'}{accesLeger.date && ` le ${dateCourte(accesLeger.date)}`}
                          </span>
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400">
                          Non accordé individuellement. Une fois accordé, l'étudiant redevable voit sa décision
                          (validé ou non) et ses matières à rattraper, sans aucune note.
                        </div>
                      )}
                      <div className={data.acces.acces_leger && !data.acces.autorise ? 'text-sky-700 dark:text-sky-300' : 'text-gray-500 dark:text-gray-400'}>
                        {effetLeger}
                      </div>
                    </div>
                    {accesLeger ? (
                      <button
                        onClick={retirerLeger}
                        disabled={majLeger}
                        className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                      >{majLeger ? 'Retrait…' : "Retirer l'accès léger"}</button>
                    ) : !formLeger && (
                      <button
                        onClick={() => { setFormLeger(true); setMessage(null); }}
                        className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 rounded font-medium"
                      >Autoriser l'accès léger</button>
                    )}
                  </div>
                  {formLeger && !accesLeger && (
                    <div className="space-y-2 pt-1">
                      <label className="block font-medium text-gray-700 dark:text-gray-300">
                        Motif <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={motifLeger}
                        onChange={(ev) => setMotifLeger(ev.target.value)}
                        rows={2}
                        maxLength={300}
                        placeholder="Ex : doit encore août, doit savoir s'il va au rattrapage"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => { setFormLeger(false); setMotifLeger(''); }}
                          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                        >Annuler</button>
                        <button
                          onClick={accorderLeger}
                          disabled={majLeger || motifLeger.trim().length < 5}
                          className="px-3 py-1.5 bg-gray-900 hover:bg-gray-700 dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900 disabled:opacity-50 text-white rounded font-medium"
                        >{majLeger ? 'Enregistrement…' : "Accorder l'accès léger"}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {edition && (
                <div className="text-xs border border-gray-200 dark:border-gray-700 rounded p-3 space-y-2 bg-gray-50 dark:bg-gray-900/40">
                  <div className="text-gray-700 dark:text-gray-300">
                    Décochez les périodes qui ne concernent pas l'étudiant et, si besoin, fixez le montant exigé
                    d'une période à la place de celui du fichier de la caisse. Les restes se recalculent à l'enregistrement.
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-gray-600 dark:text-gray-400">Arrivé en :</label>
                    <select
                      defaultValue=""
                      onChange={(ev) => { if (ev.target.value) appliquerArrivee(ev.target.value); }}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">choisir le mois…</option>
                      {MOIS_SCOLAIRES.map((m) => (
                        <option key={m} value={m}>{periodes.find((p) => p.periode === m)?.label || m}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setMontants((prev) => ({ ...prev, EXAM: '15000' }))}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      Examen : une seule session (15 000)
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 uppercase text-[10px]">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Période</th>
                      <th className="px-2 py-1.5 text-center">Concerné</th>
                      <th className="px-2 py-1.5 text-right">Attendu</th>
                      <th className="px-2 py-1.5 text-right">Payé</th>
                      <th className="px-2 py-1.5 text-right">Montant exigé</th>
                      <th className="px-2 py-1.5 text-right">Reste</th>
                      <th className="px-2 py-1.5 text-left">Statut</th>
                      <th className="px-2 py-1.5 text-left">Mise à jour</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {periodes.map((p) => {
                      const concerne = edition ? !nonConcernees.has(p.periode) : p.concernee;
                      const statut = STATUT_PERIODE[p.statut] || { label: p.statut, cls: '' };
                      return (
                        <tr key={p.periode} className={concerne ? '' : 'bg-gray-50/60 dark:bg-gray-900/30 text-gray-400'}>
                          <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">
                            {p.label}
                            {!p.requise_globale && <span className="text-[10px] text-gray-400"> · non requise</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {edition ? (
                              <input type="checkbox" checked={concerne} onChange={() => basculer(p.periode)} />
                            ) : (p.concernee ? 'Oui' : 'Non')}
                          </td>
                          <td className="px-2 py-1.5 text-right">{p.present ? fcfa(p.frais_attendus) : '—'}</td>
                          <td className="px-2 py-1.5 text-right">{p.present ? fcfa(p.frais_payes) : '—'}</td>
                          <td className="px-2 py-1.5 text-right">
                            {edition ? (
                              concerne ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="500"
                                  value={montants[p.periode] ?? ''}
                                  placeholder={p.seuil_a_jour ? fcfa(p.seuil_a_jour) : 'fichier'}
                                  onChange={(ev) => setMontants((prev) => ({ ...prev, [p.periode]: ev.target.value }))}
                                  className="w-24 px-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-right bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              ) : '—'
                            ) : p.montant_exige ? fcfa(p.montant_exige) : p.seuil_a_jour ? (
                              <span title="Seuil fixé à l'import">{fcfa(p.seuil_a_jour)}</span>
                            ) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right font-medium">
                            {p.statut === 'non_concerne' ? '—' : fcfa(p.reste)}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={statut.cls}>{statut.label}</span>
                            {p.saisie_manuelle && <span className="text-[10px] text-gray-400"> · caisse</span>}
                          </td>
                          <td className="px-2 py-1.5 text-[11px] text-gray-500">
                            {p.date_maj ? new Date(p.date_maj).toLocaleDateString('fr-FR') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <EncaissementsSection
                matricule={etudiant.matricule}
                encaissements={data.encaissements || []}
              />

              {edition && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Motif <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={motif}
                    onChange={(ev) => setMotif(ev.target.value)}
                    rows={2}
                    maxLength={300}
                    placeholder="Ex : arrivé au semestre pair (août), une seule session d'examen"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {message && (
                <div className={`text-sm rounded px-3 py-2 border ${message.ok
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}
                >
                  {message.texte}
                </div>
              )}
            </>
          )}
        </div>

        {data && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 justify-end">
            {edition ? (
              <>
                <button
                  onClick={() => { setEdition(false); setMessage(null); }}
                  className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                >Annuler</button>
                <button
                  onClick={sauver}
                  disabled={enregistrement || motif.trim().length < 5}
                  className="px-3 py-1.5 text-sm bg-gray-900 hover:bg-gray-700 dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900 disabled:opacity-50 text-white rounded font-medium"
                >{enregistrement ? 'Enregistrement…' : "Enregistrer l'aménagement"}</button>
              </>
            ) : (
              <>
                {amenagement && (
                  <button
                    onClick={supprimer}
                    disabled={retrait}
                    className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                  >{retrait ? 'Retrait…' : "Retirer l'aménagement"}</button>
                )}
                <button
                  onClick={() => { setEdition(true); setFormLeger(false); setMessage(null); }}
                  className="px-3 py-1.5 text-sm bg-gray-900 hover:bg-gray-700 dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900 text-white rounded font-medium"
                >{amenagement ? "Modifier l'aménagement" : 'Aménager les paiements'}</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Encaissements de caisse (2026-09-16) ────────────────────────────────────
// Chaque reçu saisi à la caisse est listé ici et peut être corrigé ou annulé :
// le montant est répercuté sur la période, et l'accès de l'étudiant recalculé.
const PERIODES_ENCAISSEMENT = [
  'INSC', 'NOV', 'DEC', 'JAN', 'FEV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOUT', 'EXAM',
];

const EncaissementsSection: React.FC<{
  matricule: string;
  encaissements: EncaissementManuelInfo[];
}> = ({ matricule, encaissements }) => {
  const [modifier, { isLoading: modification }] = useModifierEncaissementMutation();
  const [annuler, { isLoading: annulation }] = useAnnulerEncaissementMutation();
  const [edite, setEdite] = useState<number | null>(null);
  const [aAnnuler, setAAnnuler] = useState<number | null>(null);
  const [form, setForm] = useState({ montant: '', periode: 'MAR', reference_recu: '', remarque: '', motif: '' });
  const [motifAnnulation, setMotifAnnulation] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);

  const ouvrirEdition = (e: EncaissementManuelInfo) => {
    setAAnnuler(null);
    setMessage(null);
    setEdite(e.id);
    setForm({
      montant: e.montant,
      periode: e.periode,
      reference_recu: e.reference_recu,
      remarque: e.remarque || '',
      motif: '',
    });
  };

  const enregistrer = async (id: number) => {
    const montant = parseFloat(form.montant.replace(',', '.'));
    if (isNaN(montant) || montant <= 0) {
      setMessage({ ok: false, texte: 'Montant invalide.' });
      return;
    }
    if (form.reference_recu.trim().length < 3) {
      setMessage({ ok: false, texte: 'N° de reçu requis (3 caractères minimum).' });
      return;
    }
    if (form.motif.trim().length < 3) {
      setMessage({ ok: false, texte: 'Indiquez le motif de la correction.' });
      return;
    }
    try {
      await modifier({
        id,
        matricule,
        montant,
        periode: form.periode,
        reference_recu: form.reference_recu.trim(),
        remarque: form.remarque.trim(),
        motif: form.motif.trim(),
      }).unwrap();
      setEdite(null);
      setMessage({ ok: true, texte: "Encaissement corrigé. Le solde et l'accès de l'étudiant sont recalculés." });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || 'Erreur lors de la correction.' });
    }
  };

  const confirmerAnnulation = async (id: number) => {
    if (motifAnnulation.trim().length < 3) {
      setMessage({ ok: false, texte: "Indiquez le motif de l'annulation." });
      return;
    }
    try {
      await annuler({ id, matricule, motif: motifAnnulation.trim() }).unwrap();
      setAAnnuler(null);
      setMotifAnnulation('');
      setMessage({ ok: true, texte: "Encaissement annulé. Le montant est retiré et l'accès recalculé." });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.data?.detail || "Erreur lors de l'annulation." });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Encaissements saisis à la caisse</h3>
        <span className="text-[11px] text-gray-500 dark:text-gray-400">
          {encaissements.filter((e) => e.statut === 'actif').length} actif(s) · {encaissements.length} au total
        </span>
      </div>
      {encaissements.length === 0 ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded px-3 py-2">
          Aucun encaissement saisi à la caisse pour cet étudiant. Les montants proviennent du fichier de paiement.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 uppercase text-[10px]">
              <tr>
                <th className="px-2 py-1.5 text-left">Date</th>
                <th className="px-2 py-1.5 text-left">Période</th>
                <th className="px-2 py-1.5 text-left">N° de reçu</th>
                <th className="px-2 py-1.5 text-right">Montant</th>
                <th className="px-2 py-1.5 text-left">Saisi par</th>
                <th className="px-2 py-1.5 text-left">Statut</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {encaissements.map((e) => {
                const annuleLigne = e.statut === 'annule';
                return (
                  <React.Fragment key={e.id}>
                    <tr className={annuleLigne ? 'text-gray-400 line-through' : ''}>
                      <td className="px-2 py-1.5">{dateCourte(e.date_saisie)}</td>
                      <td className="px-2 py-1.5">{e.periode}</td>
                      <td className="px-2 py-1.5 font-mono">{e.reference_recu}</td>
                      <td className="px-2 py-1.5 text-right">{fcfa(e.montant)}</td>
                      <td className="px-2 py-1.5">{e.saisi_par_nom || '—'}</td>
                      <td className="px-2 py-1.5 no-underline">
                        {annuleLigne ? (
                          <span className="text-red-700 dark:text-red-400">Annulé</span>
                        ) : (
                          <span className="text-green-700 dark:text-green-400">Actif</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap no-underline">
                        {annuleLigne ? (
                          <span className="text-[10px] text-gray-400">
                            {e.annule_par_nom ? `par ${e.annule_par_nom}` : ''}
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => ouvrirEdition(e)}
                              className="px-2 py-0.5 text-[11px] border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                            >Modifier</button>
                            <button
                              onClick={() => { setEdite(null); setMessage(null); setAAnnuler(e.id); setMotifAnnulation(''); }}
                              className="ml-1 px-2 py-0.5 text-[11px] border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                            >Annuler</button>
                          </>
                        )}
                      </td>
                    </tr>

                    {(e.remarque || e.motif_annulation || e.motif_modification) && (
                      <tr className="text-[11px] text-gray-500 dark:text-gray-400">
                        <td className="px-2 pb-1.5" colSpan={7}>
                          {e.remarque && <span>Remarque : {e.remarque}. </span>}
                          {e.motif_modification && <span>Corrigé : {e.motif_modification}. </span>}
                          {e.motif_annulation && <span>Annulé : {e.motif_annulation}.</span>}
                        </td>
                      </tr>
                    )}

                    {edite === e.id && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={7} className="px-2 py-2">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">Période</label>
                              <select
                                value={form.periode}
                                onChange={(ev) => setForm((f) => ({ ...f, periode: ev.target.value }))}
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                {PERIODES_ENCAISSEMENT.map((p) => <option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">Montant (FCFA)</label>
                              <input
                                type="number" min="0" step="500" value={form.montant}
                                onChange={(ev) => setForm((f) => ({ ...f, montant: ev.target.value }))}
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">N° de reçu</label>
                              <input
                                value={form.reference_recu}
                                onChange={(ev) => setForm((f) => ({ ...f, reference_recu: ev.target.value }))}
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-0.5">Remarque</label>
                              <input
                                value={form.remarque}
                                onChange={(ev) => setForm((f) => ({ ...f, remarque: ev.target.value }))}
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div className="col-span-2 md:col-span-3">
                              <label className="block text-[10px] text-gray-500 mb-0.5">Motif de la correction</label>
                              <input
                                value={form.motif}
                                onChange={(ev) => setForm((f) => ({ ...f, motif: ev.target.value }))}
                                placeholder="Ex : montant du reçu mal saisi (150 000 au lieu de 105 000)"
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div className="flex items-end gap-1">
                              <button
                                onClick={() => enregistrer(e.id)}
                                disabled={modification}
                                className="px-2 py-1 text-xs bg-gray-900 hover:bg-gray-700 dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900 text-white rounded disabled:opacity-50"
                              >{modification ? 'Enregistrement…' : 'Enregistrer'}</button>
                              <button
                                onClick={() => setEdite(null)}
                                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                              >Fermer</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                    {aAnnuler === e.id && (
                      <tr className="bg-red-50 dark:bg-red-900/20">
                        <td colSpan={7} className="px-2 py-2">
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-1 min-w-[220px]">
                              <label className="block text-[10px] text-gray-500 mb-0.5">
                                Motif de l'annulation (obligatoire)
                              </label>
                              <input
                                value={motifAnnulation}
                                onChange={(ev) => setMotifAnnulation(ev.target.value)}
                                placeholder="Ex : reçu saisi deux fois"
                                className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                            <button
                              onClick={() => confirmerAnnulation(e.id)}
                              disabled={annulation}
                              className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                            >{annulation ? 'Annulation…' : `Annuler ${fcfa(e.montant)} FCFA`}</button>
                            <button
                              onClick={() => setAAnnuler(null)}
                              className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                            >Renoncer</button>
                          </div>
                          <div className="text-[11px] text-red-700 dark:text-red-300 mt-1">
                            Le montant sera retiré de la période {e.periode}. Si l'étudiant redevient redevable,
                            son accès au relevé se referme.
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {message && (
        <div className={`mt-2 text-xs rounded px-3 py-2 border ${message.ok
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'}`}
        >
          {message.texte}
        </div>
      )}
    </div>
  );
};

export default EtatPaiementModal;
